import { type FC } from 'react';
import Button from '@components/Button';
import Icon from '@components/Icon';
import InputText from '@components/InputText';
import type { ChecklistCheck, EvalWorkflowType, PromptPack } from '@root/domain/schemas/evals/eval-config.schema';
import { v4 as uuidv4 } from 'uuid';

import { cardBodyClasses, cardClasses, cardHeaderClasses, sectionHeadingClasses, subtleTextClasses } from '../classes';
import { getWorkflowPackExpectations } from '../constants';

import { MarkdownEditor } from './MarkdownEditor';
import { RolePicker } from './RolePicker';

interface PromptPacksTabProps {
  workflowType: EvalWorkflowType;
  promptPacks: PromptPack[];
  isDirty: boolean;
  isSaving: boolean;
  onChange: (packs: PromptPack[]) => void;
  onSave: () => void;
}

const createEmptyPack = (workflowType: EvalWorkflowType): PromptPack => ({
  id: uuidv4(),
  name: '',
  description: '',
  role: getWorkflowPackExpectations(workflowType).expectedPromptRoles[0]?.role ?? 'main',
  content: '',
});

const isChecklistRole = (role: string) => role.startsWith('checklist:');

export const PromptPacksTab: FC<PromptPacksTabProps> = ({
  workflowType,
  promptPacks,
  isDirty,
  isSaving,
  onChange,
  onSave,
}) => {
  const expectations = getWorkflowPackExpectations(workflowType);

  const updatePack = (id: string, patch: Partial<PromptPack>) => {
    onChange(promptPacks.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };
  const removePack = (id: string) => onChange(promptPacks.filter((p) => p.id !== id));
  const addPack = () => onChange([...promptPacks, createEmptyPack(workflowType)]);

  // Check ids must stay stable across text edits — runs record verdicts
  // against them, so only add/remove ever creates or drops an id.
  const addCheck = (pack: PromptPack) =>
    updatePack(pack.id, { checks: [...(pack.checks ?? []), { id: uuidv4(), text: '' }] });
  const removeCheck = (pack: PromptPack, checkId: string) =>
    updatePack(pack.id, { checks: (pack.checks ?? []).filter((c) => c.id !== checkId) });
  const updateCheckText = (pack: PromptPack, checkId: string, text: string) =>
    updatePack(pack.id, {
      checks: (pack.checks ?? []).map((c): ChecklistCheck => (c.id === checkId ? { ...c, text } : c)),
    });

  const missingRequired = expectations.expectedPromptRoles
    .filter((r) => r.required)
    .filter((r) => !promptPacks.some((p) => p.role === r.role));

  return (
    <div className={cardClasses}>
      <div className={cardHeaderClasses}>
        <div className="flex min-w-0 flex-col">
          <h2 className={sectionHeadingClasses}>Prompt packs</h2>
          <p className={subtleTextClasses}>{expectations.description}</p>
        </div>
        <Button
          kind="primary"
          onClick={onSave}
          isDisabled={!isDirty || isSaving}
          loading={isSaving}
          className="h-9.5 shrink-0 whitespace-nowrap"
          data-testid="button:save-prompt-packs"
        >
          Save draft
        </Button>
      </div>
      <div className={cardBodyClasses}>
        {expectations.expectedPromptRoles.length > 0 && (
          <div className="text-info-600 border-info-100 bg-info-50 rounded-md border px-3 py-2 text-xs">
            <span className="font-medium">Expected roles for this workflow type: </span>
            {expectations.expectedPromptRoles.map((r) => r.role).join(', ')}
          </div>
        )}
        {missingRequired.length > 0 && (
          <div className="text-warning-700 border-warning-200 bg-warning-50 rounded-md border px-3 py-2 text-xs">
            <span className="font-medium">Missing required prompt roles: </span>
            {missingRequired.map((r) => r.role).join(', ')}
          </div>
        )}

        {promptPacks.length === 0 && (
          <p className={subtleTextClasses}>No prompt packs yet. Add one to start editing.</p>
        )}

        <div className="flex flex-col gap-4">
          {promptPacks.map((pack, index) => (
            <div
              key={pack.id}
              className="border-info-200 flex flex-col gap-3 rounded-md border p-4"
              data-testid={`prompt-pack:${index}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-info-700 text-xs font-medium">Pack {index + 1}</div>
                <button
                  type="button"
                  className="text-info-400 hover:text-error-600 cursor-pointer transition-colors"
                  onClick={() => removePack(pack.id)}
                  aria-label={`Remove prompt pack ${index + 1}`}
                  data-testid={`button:remove-prompt-pack-${index}`}
                >
                  <Icon name="cross" className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <InputText
                  labelText="Name"
                  value={pack.name}
                  isRequired
                  onChange={(e) => updatePack(pack.id, { name: e.target.value })}
                />
                <RolePicker
                  workflowType={workflowType}
                  value={pack.role}
                  isRequired
                  onChange={(role) => updatePack(pack.id, { role })}
                  dataTestId={`prompt-pack-${index}:role`}
                />
              </div>
              <InputText
                labelText="Description"
                value={pack.description ?? ''}
                onChange={(e) => updatePack(pack.id, { description: e.target.value })}
              />
              <MarkdownEditor
                labelText={isChecklistRole(pack.role) ? 'Preamble (optional)' : 'Prompt content'}
                value={pack.content}
                onChange={(content) => updatePack(pack.id, { content })}
                placeholder={
                  isChecklistRole(pack.role)
                    ? 'Optional context prepended to every audit call for this pack...'
                    : 'Instructions for the LLM. Use markdown for structure...'
                }
                dataTestId={`prompt-pack-${index}:content`}
                name={`prompt-pack-${index}-content`}
              />
              {isChecklistRole(pack.role) && (
                <div className="flex flex-col gap-2" data-testid={`prompt-pack-${index}:checks`}>
                  <div className="text-info-700 text-xs font-medium">Checks</div>
                  {(pack.checks ?? []).length === 0 && (
                    <p className={subtleTextClasses}>
                      No checks yet. Each check is audited individually at run time and gets a forced verdict.
                    </p>
                  )}
                  {(pack.checks ?? []).map((check, checkIndex) => (
                    <div key={check.id} className="flex items-center gap-2">
                      <span
                        className="text-info-400 w-20 shrink-0 truncate font-mono text-xs"
                        title={check.id}
                        data-testid={`prompt-pack-${index}:check-${checkIndex}:id`}
                      >
                        {check.id.slice(0, 8)}
                      </span>
                      <InputText
                        className="grow"
                        aria-label={`Check ${checkIndex + 1} text`}
                        value={check.text}
                        onChange={(e) => updateCheckText(pack, check.id, e.target.value)}
                        dataTestId={`prompt-pack-${index}:check-${checkIndex}:text`}
                      />
                      <button
                        type="button"
                        className="text-info-400 hover:text-error-600 cursor-pointer transition-colors"
                        onClick={() => removeCheck(pack, check.id)}
                        aria-label={`Remove check ${checkIndex + 1} from pack ${index + 1}`}
                        data-testid={`button:remove-check-${index}-${checkIndex}`}
                      >
                        <Icon name="cross" className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <div>
                    <Button onClick={() => addCheck(pack)} className="h-9.5" data-testid={`button:add-check-${index}`}>
                      Add check
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div>
          <Button onClick={addPack} className="h-9.5" data-testid="button:add-prompt-pack">
            Add prompt pack
          </Button>
        </div>
      </div>
    </div>
  );
};
